import React, { useState, useRef, useCallback } from 'react';
import { ErrorBox } from './common/ErrorBox';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useFormContext } from 'react-hook-form';
import { InfoModal } from '../components/InfoModal';

const DimensionPicker = ({
  name,
  label,
  title,
  errors,
  detailed_label,
  validation,
  fields,
  defaultValue,
  displayUnit = null,
  conversionFactor = 1,
  description,
  inputMode = 'slider', // 'slider' or 'input'
  disabled = false
}) => {
  const { register, setValue, getValues } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get field configs with defaults
  const widthConfig = fields?.WIDTH || {};
  const heightConfig = fields?.HEIGHT || {};

  const widthMin = widthConfig.min || 0;
  const widthMax = widthConfig.max || 10000;
  const widthStep = widthConfig.step || 100;

  const heightMin = heightConfig.min || 0;
  const heightMax = heightConfig.max || 10000;
  const heightStep = heightConfig.step || 100;

  // Convert backend value to display value
  const backendToDisplay = useCallback((value) => {
    return value / conversionFactor;
  }, [conversionFactor]);

  // Convert display value to backend value
  const displayToBackend = useCallback((value) => {
    return value * conversionFactor;
  }, [conversionFactor]);

  // Get initial values from form or defaults
  const initialWidthValue = getValues(`${name}.WIDTH`) || defaultValue?.WIDTH || widthMin;
  const initialHeightValue = getValues(`${name}.HEIGHT`) || defaultValue?.HEIGHT || heightMin;

  // Local state for backend values
  const [widthBackendValue, setWidthBackendValue] = useState(initialWidthValue);
  const [heightBackendValue, setHeightBackendValue] = useState(initialHeightValue);

  // Local state for input display values (only used in input mode)
  const [widthInputValue, setWidthInputValue] = useState(() => backendToDisplay(initialWidthValue).toString());
  const [heightInputValue, setHeightInputValue] = useState(() => backendToDisplay(initialHeightValue).toString());

  const isDraggingWidthRef = useRef(false);
  const isDraggingHeightRef = useRef(false);

  // Calculate total steps for each dimension
  const widthTotalSteps = Math.floor((widthMax - widthMin) / widthStep);
  const heightTotalSteps = Math.floor((heightMax - heightMin) / heightStep);

  // Determine display unit
  const effectiveDisplayUnit = displayUnit || 'mm';

  // Set initial form values on mount
  React.useEffect(() => {
    const currentWidth = getValues(`${name}.WIDTH`);
    const currentHeight = getValues(`${name}.HEIGHT`);

    if ((currentWidth === undefined || currentWidth === '') && defaultValue?.WIDTH) {
      setValue(`${name}.WIDTH`, defaultValue.WIDTH, { shouldValidate: false });
      setWidthBackendValue(defaultValue.WIDTH);
      setWidthInputValue(backendToDisplay(defaultValue.WIDTH).toString());
    }
    if ((currentHeight === undefined || currentHeight === '') && defaultValue?.HEIGHT) {
      setValue(`${name}.HEIGHT`, defaultValue.HEIGHT, { shouldValidate: false });
      setHeightBackendValue(defaultValue.HEIGHT);
      setHeightInputValue(backendToDisplay(defaultValue.HEIGHT).toString());
    }
  }, [name, defaultValue, setValue, getValues, backendToDisplay]);

  // Width slider handlers
  const handleWidthSliderInput = useCallback((e) => {
    isDraggingWidthRef.current = true;
    const stepIndex = parseInt(e.target.value);
    const value = widthMin + (stepIndex * widthStep);
    setWidthBackendValue(value);
  }, [widthMin, widthStep]);

  const handleWidthSliderChange = useCallback((e) => {
    const stepIndex = parseInt(e.target.value);
    const value = widthMin + (stepIndex * widthStep);
    setWidthBackendValue(value);
    setValue(`${name}.WIDTH`, value, { shouldValidate: true });
    isDraggingWidthRef.current = false;
  }, [name, setValue, widthMin, widthStep]);

  // Height slider handlers
  const handleHeightSliderInput = useCallback((e) => {
    isDraggingHeightRef.current = true;
    const stepIndex = parseInt(e.target.value);
    const value = heightMin + (stepIndex * heightStep);
    setHeightBackendValue(value);
  }, [heightMin, heightStep]);

  const handleHeightSliderChange = useCallback((e) => {
    const stepIndex = parseInt(e.target.value);
    const value = heightMin + (stepIndex * heightStep);
    setHeightBackendValue(value);
    setValue(`${name}.HEIGHT`, value, { shouldValidate: true });
    isDraggingHeightRef.current = false;
  }, [name, setValue, heightMin, heightStep]);

  // Input mode handlers
  const handleWidthInputChange = useCallback((e) => {
    const displayValue = e.target.value;
    setWidthInputValue(displayValue);

    const numValue = parseFloat(displayValue);
    if (!isNaN(numValue)) {
      const backendValue = displayToBackend(numValue);
      setWidthBackendValue(backendValue);
      setValue(`${name}.WIDTH`, backendValue, { shouldValidate: true });
    }
  }, [name, setValue, displayToBackend]);

  const handleHeightInputChange = useCallback((e) => {
    const displayValue = e.target.value;
    setHeightInputValue(displayValue);

    const numValue = parseFloat(displayValue);
    if (!isNaN(numValue)) {
      const backendValue = displayToBackend(numValue);
      setHeightBackendValue(backendValue);
      setValue(`${name}.HEIGHT`, backendValue, { shouldValidate: true });
    }
  }, [name, setValue, displayToBackend]);

  // Calculate current step indices for sliders
  const widthStepIndex = Math.round((widthBackendValue - widthMin) / widthStep);
  const heightStepIndex = Math.round((heightBackendValue - heightMin) / heightStep);

  // Calculate display values
  const widthDisplayValue = backendToDisplay(widthBackendValue);
  const heightDisplayValue = backendToDisplay(heightBackendValue);

  // Format display values
  const formatDisplayValue = (value) => {
    return conversionFactor !== 1
      ? value.toFixed(1).replace(/\.0$/, '')
      : value;
  };

  // Get labels
  const widthLabel = widthConfig.label || `Breedte (${effectiveDisplayUnit})`;
  const heightLabel = heightConfig.label || `Lengte (${effectiveDisplayUnit})`;

  // Render slider input
  const renderSlider = (config, value, stepIndex, totalSteps, min, max, onInput, onChange, registerName) => (
    <>
      <div className="text-xl font-bold text-gray-900">
        {formatDisplayValue(backendToDisplay(value))}{effectiveDisplayUnit}
      </div>
      <div className="relative">
        <input
          type="range"
          min="0"
          max={totalSteps}
          value={stepIndex}
          onInput={onInput}
          onChange={onChange}
          disabled={disabled}
          className={`w-full h-2 bg-gray-200 rounded-lg appearance-none slider-thumb ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          style={{
            background: `linear-gradient(to right, rgb(31, 41, 55) 0%, rgb(31, 41, 55) ${(stepIndex / totalSteps) * 100}%, rgb(229, 231, 235) ${(stepIndex / totalSteps) * 100}%, rgb(229, 231, 235) 100%)`
          }}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{formatDisplayValue(backendToDisplay(min))}</span>
          <span>{formatDisplayValue(backendToDisplay(max))}</span>
        </div>
      </div>
      <input
        type="hidden"
        {...register(registerName, validation?.[config])}
        value={value}
      />
    </>
  );

  // Render text input
  const renderInput = (config, inputValue, onChange, registerName, placeholder) => (
    <>
      <input
        type="number"
        value={inputValue}
        onChange={onChange}
        disabled={disabled}
        className={`bg-white/80 border border-gray-300 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-gray-400 focus:border-gray-400 hover:border-gray-400 block w-full p-2.5 placeholder-gray-400 transition-all duration-200 shadow-sm ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        placeholder={placeholder}
        step={conversionFactor !== 1 ? "0.1" : "1"}
      />
      <input
        type="hidden"
        {...register(registerName, validation?.[config])}
      />
    </>
  );

  return (
    <>
      <div className="bg-white/60 rounded-2xl shadow-sm border border-gray-200/60 p-3.5 transition-all duration-200 hover:bg-white/80 hover:shadow-md backdrop-blur-sm">
        <div className="flex justify-between items-start mb-2">
          <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
          <AiOutlineInfoCircle
            className="text-gray-600 text-base cursor-pointer hover:text-gray-800 transition-colors flex-shrink-0"
            onClick={() => setIsModalOpen(true)}
          />
        </div>

        <p className="text-sm text-gray-600 mb-3">{detailed_label}</p>

        <div className="grid grid-cols-2 gap-3">
          {/* Width */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">{widthLabel}</label>

            {inputMode === 'slider' ? (
              renderSlider(
                'WIDTH',
                widthBackendValue,
                widthStepIndex,
                widthTotalSteps,
                widthMin,
                widthMax,
                handleWidthSliderInput,
                handleWidthSliderChange,
                `${name}.WIDTH`
              )
            ) : (
              renderInput(
                'WIDTH',
                widthInputValue,
                handleWidthInputChange,
                `${name}.WIDTH`,
                widthConfig.placeholder || 'Breedte'
              )
            )}

            <ErrorBox errors={errors?.WIDTH} />
          </div>

          {/* Height */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">{heightLabel}</label>

            {inputMode === 'slider' ? (
              renderSlider(
                'HEIGHT',
                heightBackendValue,
                heightStepIndex,
                heightTotalSteps,
                heightMin,
                heightMax,
                handleHeightSliderInput,
                handleHeightSliderChange,
                `${name}.HEIGHT`
              )
            ) : (
              renderInput(
                'HEIGHT',
                heightInputValue,
                handleHeightInputChange,
                `${name}.HEIGHT`,
                heightConfig.placeholder || 'Lengte'
              )
            )}

            <ErrorBox errors={errors?.HEIGHT} />
          </div>
        </div>
      </div>

      <InfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
      >
        <p className="text-sm leading-relaxed">{description}</p>
      </InfoModal>
    </>
  );
};

export default DimensionPicker;
